const BRITISH_TIME_ZONE = "Europe/London";

// Some API timestamps (e.g. request `createdOn`) omit a timezone designator
// because the backend now sends them as UK (Europe/London) wall-clock local
// time, which JS would otherwise parse in the viewer's own local timezone.
const HAS_TIMEZONE_DESIGNATOR = /(Z|[+-]\d{2}:?\d{2})$/i;
const LOCAL_ISO_TIMESTAMP =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/;
const BRITISH_DATE_TIME_PARTS = new Intl.DateTimeFormat("en-GB", {
  timeZone: BRITISH_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function getBritishWallClockUtcMs(date: Date): number {
  const parts = BRITISH_DATE_TIME_PARTS.formatToParts(date);
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? "0");

  return Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
    date.getUTCMilliseconds()
  );
}

function parseBritishLocalIsoString(isoString: string): Date {
  const match = LOCAL_ISO_TIMESTAMP.exec(isoString);
  if (!match) return new Date(isoString);

  const [, year, month, day, hour, minute, second = "00", fraction = "0"] = match;
  const milliseconds = Number(fraction.padEnd(3, "0"));
  const intendedUtcMs = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    milliseconds
  );
  const candidate = new Date(intendedUtcMs);
  const britishWallClockUtcMs = getBritishWallClockUtcMs(candidate);
  const correctionMs = britishWallClockUtcMs - intendedUtcMs;

  return new Date(candidate.getTime() - correctionMs);
}

export function toDate(isoString: string): Date {
  if (HAS_TIMEZONE_DESIGNATOR.test(isoString)) return new Date(isoString);
  return parseBritishLocalIsoString(isoString);
}

/** How long a request can sit claimed before another agent can take it over. */
const CLAIM_STALE_MS = 15 * 60 * 1000;

/** Whether the given timestamp is more than CLAIM_STALE_MS in the past. */
export function isClaimStale(isoString: string): boolean {
  return Date.now() - toDate(isoString).getTime() > CLAIM_STALE_MS;
}

/** Returns a "Xm ago" / "Xh ago" / "Xd ago" string for the given timestamp. */
export function formatTimeAgo(isoString: string): string {
  const diffMs = Math.max(0, Date.now() - toDate(isoString).getTime());
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

/**
 * Formats a timestamp using `toLocaleString("en-GB", options)`, forcing the
 * Europe/London timezone so British time renders correctly regardless of
 * the viewer's browser/OS timezone.
 */
export function formatBritishDateTime(isoString: string, options: Intl.DateTimeFormatOptions): string {
  return toDate(isoString).toLocaleString("en-GB", { ...options, timeZone: BRITISH_TIME_ZONE });
}
